import { useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import moment from 'moment'
import {
  InsiService,
  SearchTraitsInsiDTO,
  SearchVitalCardInsiDTO,
  InsResponseDTO,
} from '@services/InsiService'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  setInsError,
  setInsInfo,
  setSelectedInsiSearchResult,
} from '@state/reducers/patientsReducer'
import { CreatePatientDTO, MemberSituationDTO } from '@services/dtos'
import { CouvertureDto, PatientReadSourceEnum } from '../../common/interfaces'
import { ActiveFormType } from './ManagePatientComponent'
import { InsiFormValues } from './PatientInsiInfos'

type SearchMode = 'traits' | 'vitalCard'

type SelectedMemberSituation = {
  situation: MemberSituationDTO
} | null

type Props = {
  open: boolean
  onClose: () => void
  memberSituation: SelectedMemberSituation
  getPatientValues: () => Promise<CreatePatientDTO | null>
  getCoverageValues: () => Promise<CouvertureDto | null>
  getInsiFormValues: () => Promise<InsiFormValues | null>
  onSearchSuccess: (result: InsResponseDTO) => void
  setActiveForm: (form: ActiveFormType) => void
}

const sanitize = (value?: string | number | null) =>
  value !== undefined && value !== null ? String(value).replace(/\s/g, '') : ''

const resolveBirthDate = (birthDateString: string, format: string) => {
  if (birthDateString) {
    const parsed = moment(birthDateString, format)
    return parsed.format('YYYY-MM-DD')
  }

  // if (patientValues.birthDateStr) {
  //   const parsed = moment(patientValues.birthDateStr, 'DD-MM-YYYY', true)
  //   if (parsed.isValid()) {
  //     return parsed.format('YYYY-MM-DD')
  //   }
  // }

  return ''
}

const InsiVerificationComponent: React.FC<Props> = ({
  open,
  onClose,
  memberSituation,
  getPatientValues,
  getCoverageValues,
  getInsiFormValues,
  onSearchSuccess,
  setActiveForm,
}) => {
  const dispatch = useAppDispatch()
  const { insError, insInfo } = useAppSelector(({ patients }) => ({
    insError: patients.insError,
    insInfo: patients.insInfo,
  }))
  const [searchMode, setSearchMode] = useState<SearchMode>('traits')
  const [isSearching, setIsSearching] = useState(false)

  const resetInsiFeedback = () => {
    dispatch(setInsError(null))
    dispatch(setInsInfo(''))
  }

  const handleSearchModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: SearchMode | null,
  ) => {
    if (!newMode) {
      return
    }
    setSearchMode(newMode)
    resetInsiFeedback()
  }

  const handleSearch = async () => {
    if (isSearching) {
      return
    }

    resetInsiFeedback()

    if (!memberSituation?.situation) {
      dispatch(
        setInsError(
          'Veuillez sélectionner un médecin avec une situation valide avant de lancer la recherche INSi',
        ),
      )
      return
    }

    const patientValues = await getPatientValues?.()
    if (!patientValues) {
      dispatch(
        setInsError(
          'Veuillez compléter ou sauvegarder les informations du patient avant de lancer la recherche INSi',
        ),
      )
      return
    }

    const insiValues = await getInsiFormValues()
    const coverageValues = await getCoverageValues?.()

    let birthDateString = ''
    const format = 'DD/MM/YYYY'

    if (insiValues?.birthDate) {
      birthDateString = insiValues.birthDate
    } else if (patientValues.birthDate) {
      birthDateString = moment(patientValues.birthDate).format('DD/MM/YYYY')
    } else if (coverageValues?.beneficaireDateNaissance) {
      birthDateString = coverageValues.beneficaireDateNaissance
    }

    const birthDate = resolveBirthDate(birthDateString, format)
    if (!birthDate) {
      dispatch(
        setInsError(
          'La date de naissance du patient est requise pour la recherche INSi',
        ),
      )
      setActiveForm('PatientInfos')
      return
    }

    const psModel = {
      psNumeroFacturation:
        memberSituation.situation.numIdentificationPSFacturation,
      psIdentStructure: memberSituation.situation.numIdentificationStructure,
      authentificationMode: '1',
    }

    setIsSearching(true)

    try {
      const fichierInjecte = { fichierBase64: '' }
      const assertions = { assertionPs: '', assertionVitale: '' }
      let response: InsResponseDTO | null = null

      const numeroSerieCV = coverageValues?.numeroSerieCv || ''

      const beneficiaireNir = coverageValues?.beneficiaireNir || ''
      const beneficiaireSanitizedSsn = sanitize(beneficiaireNir)

      const assureSsn = patientValues.ssn
      const assureSanitizedSsn = sanitize(assureSsn)
      const isApcv = coverageValues?.readSource === PatientReadSourceEnum.Apcv

      if (searchMode === 'traits') {
        const firstName =
          insiValues?.firstName ||
          coverageValues?.beneficiairePrenom ||
          patientValues.firstName ||
          ''

        const prenoms = firstName
          .split(' ')
          .map((p: string) => p.trim())
          .filter((p: string) => p.length > 0)

        const lastName =
          insiValues?.lastName ||
          patientValues.lastName ||
          coverageValues?.beneficiaireNom ||
          ''

        if (!lastName || !prenoms.length) {
          dispatch(
            setInsError(
              'Le nom et au moins un prénom sont requis pour la recherche par traits',
            ),
          )
          return
        }

        if (!patientValues.sex) {
          dispatch(
            setInsError('Le sexe est requis pour la recherche par traits'),
          )
          return
        }

        response = await InsiService.findByTraits({
          psModel,
          insTraits: {
            beneficiaireNom: insiValues?.lastName || '',
            beneficiaireSexe: insiValues?.sex,
            beneficiaireDateNaissance: birthDate,
            beneficiaireLieu: insiValues?.birthPlace,
            beneficiairePrenoms: prenoms,
            beneficiaireRang: coverageValues?.rangNaissance || '',
            beneficiaireNumSecu: beneficiaireSanitizedSsn,
            assureNumSecu: assureSanitizedSsn,
          },
          fichierInjecte,
          assertions,
          idJfse: memberSituation.situation.idJfse,
          isApcv,
        } as SearchTraitsInsiDTO)
      } else {
        response = await InsiService.findByVitalCard({
          psModel,
          insCV: {
            beneficiaireDateNaissance: birthDate,
            beneficiaireRang: coverageValues?.rangNaissance?.toString() || '1',
            beneficiaireNom: coverageValues?.beneficaireNomUsuel || '',
            beneficiaireNomNaissance: coverageValues?.beneficiaireNom,
            numeroSerieCV,
            assureNomUsage: coverageValues?.assureNomUsuel || '',
            assureNomNaissance: coverageValues?.assureNom,
            beneficiaireNumSecu: beneficiaireSanitizedSsn,
            assureNumSecu: assureSanitizedSsn,
          },
          fichierInjecte,
          assertions,
          idJfse: memberSituation.situation.idJfse,
          isApcv,
        } as SearchVitalCardInsiDTO)
      }
      const message =
        response.message.reponse.erreurMessage ||
        response.message.reponse.erreurCode
      const isError = response.message.reponse.erreur
      if (isError) {
        dispatch(setInsError(message))
        return
      }
      if (message) {
        dispatch(setInsInfo(message))
      } else {
        dispatch(setInsInfo(''))
      }

      dispatch(setSelectedInsiSearchResult(response))
      onSearchSuccess(response)
      onClose()
      dispatch(setInsError(null))
    } catch (error: any) {
      dispatch(
        setInsError(
          error?.message ||
            'Erreur lors de la recherche INSi. Veuillez réessayer.',
        ),
      )
    } finally {
      setIsSearching(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Recherche INSi</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Choisissez une méthode de recherche INSi pour récupérer les
            informations nécessaires à la vérification.
          </Typography>
          <ToggleButtonGroup
            value={searchMode}
            exclusive
            size="small"
            onChange={handleSearchModeChange}
          >
            <ToggleButton value="traits">Recherche par traits</ToggleButton>
            <ToggleButton value="vitalCard">Carte Vitale</ToggleButton>
          </ToggleButtonGroup>
          {searchMode === 'vitalCard' && (
            <Alert severity="info">
              Une lecture de carte Vitale est nécessaire afin de récupérer le
              numéro de série.
            </Alert>
          )}
          {insError && <Alert severity="error">{insError}</Alert>}
          {insInfo && <Alert severity="info">{insInfo} </Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} color="inherit">
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={isSearching}
          startIcon={isSearching ? <CircularProgress size={18} /> : undefined}
        >
          {isSearching ? 'Recherche en cours...' : 'Lancer la recherche'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default InsiVerificationComponent
